<template>
  <v-menu offset-y :close-on-content-click="false" location="bottom end" v-model="deschis">
    <template #activator="{ props }">
      <v-btn v-bind="props" icon variant="text" color="white" class="notif-btn" title="Notificări">
        <v-badge v-if="nrNecitite > 0" :content="nrNecitite > 99 ? '99+' : nrNecitite"
                 color="#ff5722" offset-x="2" offset-y="2">
          <v-icon>mdi-bell-outline</v-icon>
        </v-badge>
        <v-icon v-else>mdi-bell-outline</v-icon>
      </v-btn>
    </template>

    <v-card class="notif-card" elevation="0">
      <div class="notif-head">
        <span class="notif-head-ic"><v-icon size="18" color="#fff">mdi-bell-ring-outline</v-icon></span>
        <div class="notif-head-txt">
          <span class="notif-title">Notificări</span>
          <span class="notif-sub">{{ nrNecitite > 0 ? nrNecitite + ' necitite' : 'Ești la zi' }}</span>
        </div>
        <v-spacer />
        <button v-if="nrNecitite > 0" class="notif-mark-all" @click="citesteTot" title="Marchează toate citite">
          <v-icon size="16">mdi-check-all</v-icon>
        </button>
      </div>

      <div class="notif-list">
        <div v-if="!notificari.length" class="notif-empty">
          <div class="empty-ic"><v-icon size="30" color="#cbd5e1">mdi-bell-check-outline</v-icon></div>
          <span class="empty-t">Nicio notificare</span>
          <span class="empty-s">Aici vor apărea aprobările și noutățile tale.</span>
        </div>

        <button v-for="n in notificari" :key="n.ID" class="notif-item"
                :class="{ unread: !n.Citita }" @click="onClick(n)">
          <span class="notif-accent" :style="{ background: culoare(n.Severitate) }" />
          <span class="notif-ico" :style="{ background: culoare(n.Severitate) + '18', color: culoare(n.Severitate) }">
            <v-icon size="19">{{ n.Icon || 'mdi-bell-outline' }}</v-icon>
          </span>
          <span class="notif-body">
            <span class="notif-item-title">{{ n.Titlu }}<span v-if="!n.Citita" class="notif-dot" /></span>
            <span class="notif-item-msg">{{ n.Mesaj }}</span>
            <span class="notif-item-time"><v-icon size="11" class="mr-1">mdi-clock-outline</v-icon>{{ timpRelativ(n.DataCreare) }}</span>
          </span>
          <v-icon class="notif-del" size="16" @click.stop="sterge(n)">mdi-close</v-icon>
        </button>
      </div>
    </v-card>
  </v-menu>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import * as signalR from "@microsoft/signalr";
import NotificariService, { Notificare } from "@/services/NotificariService";
import AppSettings from "@/settings/appSettings";

export default defineComponent({
  name: "NotificariBell",
  data() {
    return {
      deschis: false,
      nrNecitite: 0,
      notificari: [] as Notificare[],
      timer: null as ReturnType<typeof setInterval> | null,
      connection: null as signalR.HubConnection | null,
    };
  },
  mounted() {
    this.incarca();
    // Polling rămâne ca fallback (dacă hub-ul pică), dar rărit la 60s — push-ul SignalR
    // aduce notificările HR instant.
    this.timer = setInterval(this.incarca, 60000);
    this.initRealtime();
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer);
    this.connection?.stop().catch(() => undefined);
  },
  methods: {
    // Conexiune realtime la hub-ul Portal HR. Push-ul instant funcționează doar pentru
    // notificările emise de PortalHR (824); restul modulelor rămân pe polling până la un
    // backplane SignalR comun. Reconnect automat + polling fallback — fără erori care să
    // spargă pagina dacă hub-ul e indisponibil.
    initRealtime() {
      const hubUrl = AppSettings.API_Url_PortalHRSignalR || "http://localhost:824/notificariHub";
      try {
        this.connection = new signalR.HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: () => localStorage.getItem("ERA_Jwt") || "",
          })
          .withAutomaticReconnect()
          .build();

        this.connection.on("notificareNoua", () => this.incarca());

        this.connection.start().catch(() => {
          /* fără realtime dacă hub-ul nu e disponibil — polling rămâne fallback */
        });
      } catch {
        /* nu blocăm clopoțelul dacă inițializarea SignalR eșuează */
      }
    },
    async incarca() {
      try {
        const { data } = await NotificariService.get();
        this.nrNecitite = data?.NrNecitite ?? 0;
        this.notificari = data?.Notificari ?? [];
      } catch {
        /* silentios: clopotelul nu trebuie sa strice shell-ul */
      }
    },
    culoare(sev?: string | null): string {
      switch ((sev || "info").toLowerCase()) {
        case "success": return "#2e7d32";
        case "warning": return "#ed6c02";
        case "danger": return "#d32f2f";
        default: return "#ff5722";
      }
    },
    timpRelativ(data: string): string {
      const d = new Date(data);
      const s = Math.floor((Date.now() - d.getTime()) / 1000);
      if (s < 60) return "acum";
      if (s < 3600) return `acum ${Math.floor(s / 60)} min`;
      if (s < 86400) return `acum ${Math.floor(s / 3600)} h`;
      if (s < 604800) return `acum ${Math.floor(s / 86400)} zile`;
      return d.toLocaleDateString("ro-RO");
    },
    async onClick(n: Notificare) {
      if (!n.Citita) {
        try {
          await NotificariService.marcheazaCitit(n.ID);
          n.Citita = true;
          this.nrNecitite = Math.max(0, this.nrNecitite - 1);
        } catch { /* ignore */ }
      }
      if (n.Ruta) {
        let params: Record<string, any> = {};
        if (n.RutaParams) {
          try { params = JSON.parse(n.RutaParams); } catch { params = {}; }
        }
        this.deschis = false;
        try {
          this.$router.push({ name: n.Ruta, params });
        } catch {
          this.$router.push({ name: n.Ruta });
        }
      }
    },
    async citesteTot() {
      try {
        await NotificariService.citesteTot();
        this.notificari.forEach((n) => (n.Citita = true));
        this.nrNecitite = 0;
      } catch { /* ignore */ }
    },
    async sterge(n: Notificare) {
      try {
        await NotificariService.sterge(n.ID);
        this.notificari = this.notificari.filter((x) => x.ID !== n.ID);
        if (!n.Citita) this.nrNecitite = Math.max(0, this.nrNecitite - 1);
      } catch { /* ignore */ }
    },
  },
});
</script>

<style scoped>
.notif-card { width: 400px; max-width: 92vw; border-radius: 16px; overflow: hidden; border: 1px solid #e9edf3; box-shadow: 0 18px 50px rgba(15,23,42,.18); }
.notif-head {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 16px; background: linear-gradient(120deg,#ff5722,#ff8a5c); color: #fff;
}
.notif-head-ic { width: 36px; height: 36px; border-radius: 11px; background: rgba(255,255,255,.22); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.notif-head-txt { display: flex; flex-direction: column; line-height: 1.15; }
.notif-title { font-weight: 800; font-size: 15px; }
.notif-sub { font-size: 11.5px; opacity: .9; margin-top: 1px; }
.notif-mark-all {
  display: flex; align-items: center; justify-content: center; width: 32px; height: 32px;
  background: rgba(255,255,255,.2); border: none; color: #fff; border-radius: 9px; cursor: pointer; transition: background .15s;
}
.notif-mark-all:hover { background: rgba(255,255,255,.34); }

.notif-list { max-height: 440px; overflow-y: auto; background: #fbfcfe; }
.notif-empty {
  display: flex; flex-direction: column; align-items: center; text-align: center;
  padding: 40px 24px; color: #94a3b8;
}
.empty-ic { width: 60px; height: 60px; border-radius: 50%; background: #f1f5f9; display: flex; align-items: center; justify-content: center; margin-bottom: 12px; }
.empty-t { font-size: 14px; font-weight: 700; color: #64748b; }
.empty-s { font-size: 12px; margin-top: 4px; max-width: 240px; }

.notif-item {
  display: flex; align-items: flex-start; gap: 12px; width: 100%;
  padding: 13px 16px 13px 18px; border: none; background: transparent; cursor: pointer;
  border-bottom: 1px solid #eef1f6; text-align: left; position: relative;
  transition: background .15s;
}
.notif-item:last-child { border-bottom: none; }
.notif-item:hover { background: #fff; }
.notif-item.unread { background: #fff7f3; }
.notif-item.unread:hover { background: #fff1ea; }
.notif-accent { position: absolute; left: 0; top: 0; bottom: 0; width: 3px; opacity: 0; }
.notif-item.unread .notif-accent { opacity: 1; }
.notif-ico {
  flex: 0 0 auto; width: 38px; height: 38px; border-radius: 11px;
  display: flex; align-items: center; justify-content: center;
}
.notif-body { display: flex; flex-direction: column; gap: 3px; min-width: 0; flex: 1; }
.notif-item-title { font-weight: 700; font-size: 13.5px; color: #1e293b; display: flex; align-items: center; }
.notif-item-msg {
  font-size: 12.5px; color: #64748b; line-height: 1.4;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.notif-item-time { font-size: 11px; color: #94a3b8; margin-top: 3px; display: flex; align-items: center; }
.notif-dot {
  width: 7px; height: 7px; border-radius: 50%; background: #ff5722; margin-left: 8px; flex: 0 0 auto;
}
.notif-del {
  position: absolute; top: 10px; right: 10px; color: #cbd5e1; opacity: 0;
  transition: opacity .15s, color .15s; border-radius: 6px;
}
.notif-item:hover .notif-del { opacity: 1; }
.notif-del:hover { color: #dc2626; background: #fef2f2; }
</style>
